import React, { Component } from "react";
import { Link } from "react-router-dom";
import _ from "lodash";
import marked from "marked";
import cn from "classnames";
import { getHomeData } from "../services/api";
import removeHash from "../services/remove-hash";
import { parseOnlyLetters, parseOnlyNumbers } from "../util/strings";
import MobileNotAvailableModal from "./mobile-not-available-modal";
import { responsiveViews } from "../route-utils";

const EXTERNAL_RE = /^https?:\/\//i;

function isResponsivePath(path) {
  if (!path) return false;
  const first = path.split("/").filter(Boolean)[0] || "";
  return first === "welcome" || responsiveViews.includes(first);
}

export default class ResponsiveWelcome extends Component {
  constructor(props) {
    super(props);
    this.state = { leftSideIcons: null, postIt: null, loading: true, notAvailableVisible: false };
    this.showNotAvailable = this.showNotAvailable.bind(this);
    this.hideNotAvailable = this.hideNotAvailable.bind(this);
  }

  showNotAvailable() { this.setState({ notAvailableVisible: true }); }
  hideNotAvailable() { this.setState({ notAvailableVisible: false }); }

  componentDidMount() {
    getHomeData()
      .then((data) => {
        this.setState({
          leftSideIcons: data.leftSideIcons || null,
          postIt: data.postIt || null,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  tryParseIcon(suffix, data, out, prev, columnRow) {
    const imageKey = "icon" + suffix;
    const linkKey = "link" + suffix;
    const videoKey = "video" + suffix;
    const prevIconKey = "iconSize" + prev;
    const prevImageKey = "icon" + prev;
    const iconSizeKey = "iconSize" + suffix;
    const performanceLogKey = "performanceLogLink" + suffix;
    const image = data[imageKey];
    const prevIconImage = data[prevImageKey];
    const videoId = data[videoKey];
    const iconSize = data[iconSizeKey];
    const prevIconSize = data[prevIconKey];
    let columnClass = "";
    let gridRow = { gridRow: `${suffix}` };

    if (!image) return;
    if (!_.has(image, "fields.file.url")) return;
    const url = image.fields.file.url;
    const link = videoId
      ? `/video?vimeoId=${videoId}`
      : data[performanceLogKey] || data[linkKey] || "";

    columnRow.push(`'${suffix}'`);

    if (iconSize !== "default" && iconSize !== undefined) {
      columnClass = iconSize;
      if (prevIconSize && prevIconSize === iconSize && prevIconImage) {
        columnClass = "small side-to-side";
        gridRow = { gridRow: `${prev}` };
        columnRow.pop();
      }
    }
    if (
      (prevIconSize === undefined && iconSize === "small") ||
      (prevIconSize === "small" && iconSize === "default")
    ) {
      columnClass = "";
    }
    const path = removeHash(link);
    const normalizedUrl = url.startsWith("//") ? "https:" + url : url;
    const imgEl = <img className="ui medium centered image" src={normalizedUrl} alt={suffix} />;
    let linkEl;
    if (EXTERNAL_RE.test(path)) {
      linkEl = <a href={path} target="_blank" rel="noopener noreferrer" className="icon-link">{imgEl}</a>;
    } else if (isResponsivePath(path)) {
      linkEl = <Link to={path || "/welcome"} className="icon-link">{imgEl}</Link>;
    } else {
      linkEl = <span className="icon-link" onClick={this.showNotAvailable} role="button" tabIndex={0}>{imgEl}</span>;
    }

    out.push(
      <div
        className={`row ${columnClass} icon-link`}
        key={imageKey}
        style={gridRow}
      >
        {linkEl}
      </div>
    );
  }

  parseLeftSideIcons(data, result, leftSideIconsColumns) {
    const iconkeys = Object.keys(data);
    const keysLength = iconkeys.length;
    for (let i = 0; i <= keysLength; i++) {
      let icon = iconkeys[i];
      if (i >= 1 || i <= keysLength)
        leftSideIconsColumns.prevIconIndex = parseOnlyNumbers(iconkeys[i - 1]);
      if (icon && parseOnlyLetters(icon) === "icon") {
        let iconArea = parseOnlyNumbers(icon);
        this.tryParseIcon(
          iconArea,
          data,
          result,
          leftSideIconsColumns.prevIconIndex,
          leftSideIconsColumns.columnRow
        );
      }
    }
  }

  parsePostIt(icon, result) {
    if (!icon) return;
    let { url, display, content, style, videoId, id } = icon;
    const type = style.slice(0, -2);
    const typeValue = style.slice(-1);
    let background = `url('')`;
    if (!display || (style.indexOf("paper") !== -1 && display && id !== "top"))
      return;
    if (type === "paper") background = null;
    if (!content) return;
    content = marked(content);
    const cx = cn("post-it", id, type, typeValue);
    const contentCn = cn("content", type);
    const link = videoId ? `/video?vimeoId=${videoId}` : url;
    if (link) {
      const path = removeHash(link);
      result.push(
        <div
          style={background !== null ? { backgroundImage: background } : {}}
          className={cx}
          key={id}
        >
          <Link to={path}>
            <div
              className={contentCn}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </Link>
        </div>
      );
    }
  }

  getItems(icons, type) {
    if (!icons) return <div />;
    const keys = Object.keys(icons);
    const result = [];
    const leftSideIconsColumns = {
      columnRow: [],
      prevIconIndex: 0,
      columnCount: 0,
      leftSideIconStyle: {},
    };

    keys.forEach((_key) => {
      const icon = icons[_key] || false;
      if (type === "icon") {
        this.parseLeftSideIcons(icon, result, leftSideIconsColumns);
      } else {
        this.parsePostIt(icon, result);
      }
    });

    if (type === "icon") {
      leftSideIconsColumns.columnCount = leftSideIconsColumns.columnRow.length;
      leftSideIconsColumns.columnRow = `"${leftSideIconsColumns.columnRow.toString()}"`.replace(
        /[,"]/g,
        ""
      );
      const columnSizeStyle =
        leftSideIconsColumns.columnCount === 5
          ? "extendedColumn"
          : leftSideIconsColumns.columnCount === 6
          ? "extraColumn"
          : "defaultColumn";
      const leftSideIconStyle = {
        gridTemplateAreas: leftSideIconsColumns.columnRow,
      };
      return result.length > 0 ? (
        <div className={`left-side-icons ${columnSizeStyle}`} style={leftSideIconStyle}>
          {result}
        </div>
      ) : (
        <div />
      );
    }

    return result.length > 0 ? <div className="postit-col">{result}</div> : <div />;
  }

  render() {
    const { leftSideIcons, postIt, loading, notAvailableVisible } = this.state;

    if (loading) {
      return (
        <div className="responsive-welcome-loading">
          <div className="responsive-welcome-spinner" />
        </div>
      );
    }

    return (
      <div id="leftside-icons-wrapper">
        {postIt && this.getItems(postIt, "post")}
        {leftSideIcons && this.getItems(leftSideIcons, "icon")}
        <MobileNotAvailableModal visible={notAvailableVisible} onClose={this.hideNotAvailable} />
      </div>
    );
  }
}
